// Auth helpers built on top of Amplify v6. Wraps signIn / signOut / token refresh
// and writes the rw_token cookie on .redwoodoperations.com so other subdomains
// can read it.

import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  fetchAuthSession,
  getCurrentUser,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  confirmSignIn as amplifyConfirmSignIn,
  type SignInOutput,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { setIdTokenCookie, clearIdTokenCookie } from './cookie.js';

export interface CurrentUser {
  sub: string;
  email: string;
  name: string;
  groups: string[];
}

/** Sync the current ID token to the rw_token cookie. Call after sign in / refresh. */
export async function syncCookieFromSession(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (idToken) {
      setIdTokenCookie(idToken);
      return idToken;
    }
  } catch (err) {
    console.warn('failed to sync cookie from session', err);
  }
  return null;
}

/** Get the currently signed-in user from the cached session, or null. */
export async function getCachedUser(): Promise<CurrentUser | null> {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    if (!idToken) return null;
    const payload = idToken.payload as Record<string, unknown>;
    return {
      sub: String(payload.sub ?? ''),
      email: String(payload.email ?? ''),
      name: String(payload.name ?? payload.email ?? ''),
      groups: (payload['cognito:groups'] as string[] | undefined) ?? [],
    };
  } catch {
    return null;
  }
}

export async function signIn(email: string, password: string): Promise<SignInOutput> {
  // Amplify throws "There is already a signed in user" if a stale session exists.
  // Clear it first so a fresh signIn always works.
  try {
    await getCurrentUser();
    await amplifySignOut();
    clearIdTokenCookie();
  } catch {
    // No existing user — normal path
  }
  return amplifySignIn({ username: email, password });
}

export async function confirmNewPassword(newPassword: string): Promise<SignInOutput> {
  return amplifyConfirmSignIn({ challengeResponse: newPassword });
}

export async function signOut(): Promise<void> {
  await amplifySignOut();
  clearIdTokenCookie();
}

export async function requestPasswordReset(email: string): Promise<void> {
  await amplifyResetPassword({ username: email });
}

export async function confirmPasswordReset(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  await amplifyConfirmResetPassword({
    username: email,
    confirmationCode: code,
    newPassword,
  });
}

/** Set up listeners that auto-write the cookie whenever Amplify refreshes the token. */
export function startSessionListener(): void {
  Hub.listen('auth', async ({ payload }) => {
    if (payload.event === 'tokenRefresh' || payload.event === 'signedIn') {
      await syncCookieFromSession();
    }
    if (payload.event === 'signedOut' || payload.event === 'tokenRefresh_failure') {
      clearIdTokenCookie();
    }
  });
}

/** Best-effort check on app boot — if there's a usable Cognito session
 *  (refreshing expired tokens when possible), sync the cookie and return the user.
 *
 *  Note: we intentionally do NOT gate on getCurrentUser() here. In Amplify v6
 *  getCurrentUser() can throw once the access token has expired, even when a
 *  valid refresh token is still present — which is exactly the case when a user
 *  is bounced here to have their token refreshed. fetchAuthSession() (inside
 *  getCachedUser/syncCookieFromSession) transparently refreshes the tokens, so
 *  driving bootstrap off it lets a silent refresh happen and the user gets
 *  bounced back to their original location instead of being shown the login form.
 */
export async function bootstrap(): Promise<CurrentUser | null> {
  const user = await getCachedUser(); // fetchAuthSession → auto-refreshes if needed; null if no session
  if (!user) return null;
  await syncCookieFromSession();
  return user;
}
